"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
  useEffect,
} from "react";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";

export interface MusicObject {
  id: string;
  musicFile: string;
  title: string;
  artist?: string;
  description?: string;
  coverImage?: string;
  duration?: string;
}

interface DownloadState {
  isDownloading: boolean;
  progress: number;
  isCompleted: boolean;
  error?: string;
  filePath?: string;
  fileSize?: number;
}

export interface StoredMusicFile {
  id: string;
  music: MusicObject;
  filePath: string;
  fileSize: number;
  downloadDate: string;
}

interface DownloadMusicContextType {
  downloadStates: Record<string, DownloadState>;
  downloadHistory: StoredMusicFile[];
  currentlyDownloading: Record<string, MusicObject>;
  downloadMusic: (music: MusicObject) => Promise<void>;
  cancelDownload: (musicId: string) => void;
  clearDownload: (musicId: string) => void;
  removeFromHistory: (musicId: string) => void;
  deleteStoredFile: (musicId: string) => Promise<void>;
  getStorageInfo: () => Promise<{ totalFiles: number; totalSize: number }>;
}

const DownloadMusicContext = createContext<
  DownloadMusicContextType | undefined
>(undefined);

export const useDownloadMusic = () => {
  const context = useContext(DownloadMusicContext);
  if (!context) {
    throw new Error(
      "useDownloadMusic must be used within a DownloadMusicProvider"
    );
  }
  return context;
};

interface DownloadMusicProviderProps {
  children: ReactNode;
}

export const DownloadMusicProvider: React.FC<DownloadMusicProviderProps> = ({
  children,
}) => {
  const [downloadStates, setDownloadStates] = useState<
    Record<string, DownloadState>
  >({});
  const [downloadControllers, setDownloadControllers] = useState<
    Record<string, AbortController>
  >({});
  const [downloadHistory, setDownloadHistory] = useState<StoredMusicFile[]>([]);
  const [currentlyDownloading, setCurrentlyDownloading] = useState<
    Record<string, MusicObject>
  >({});

  // Initialize and load stored files
  useEffect(() => {
    initializeFileSystem();
  }, []);

  const initializeFileSystem = async () => {
    try {
      // Create music directory if it doesn't exist
      await Filesystem.mkdir({
        path: "music",
        directory: Directory.Data,
        recursive: true,
      });
    } catch (error) {
      console.error("Failed to initialize file system:", error);
    } finally {
      // Load stored music files
      await loadStoredFiles();
    }
  };

  const loadStoredFiles = async () => {
    try {
      // Read the music directory
      const result = await Filesystem.readdir({
        path: "music",
        directory: Directory.Data,
      });

      console.log("result", result);

      const storedFiles: StoredMusicFile[] = [];

      // Load metadata for each file
      for (const file of result.files) {
        if (file.name.endsWith(".json")) {
          try {
            const metadataContent = await Filesystem.readFile({
              path: `music/${file.name}`,
              directory: Directory.Data,
              encoding: Encoding.UTF8,
            });

            const metadata = JSON.parse(metadataContent.data as string);
            storedFiles.push(metadata);
          } catch (error) {
            console.error(`Failed to load metadata for ${file.name}:`, error);
          }
        }
      }

      console.log("storedFiles", storedFiles);
      setDownloadHistory(storedFiles);
    } catch (error) {
      console.error("Failed to load stored files:", error);
    }
  };

  const downloadMusic = useCallback(async (music: MusicObject) => {
    const controller = new AbortController();
    const fileName = `${music.id}.mp3`;
    const metadataFileName = `${music.id}.json`;

    // Set up download tracking
    setDownloadControllers((prev) => ({
      ...prev,
      [music.id]: controller,
    }));

    setCurrentlyDownloading((prev) => ({
      ...prev,
      [music.id]: music,
    }));

    setDownloadStates((prev) => ({
      ...prev,
      [music.id]: {
        isDownloading: true,
        progress: 0,
        isCompleted: false,
        error: undefined,
      },
    }));

    try {
      // Fetch with real progress tracking
      const response = await fetch(music.musicFile, {
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${fileName}: ${response.statusText}`);
      }

      // Get total file size for progress calculation
      const contentLength = response.headers.get("content-length");
      const totalBytes = contentLength ? Number.parseInt(contentLength, 10) : 0;

      // If we can't determine file size, we'll still download but without accurate progress
      if (!totalBytes) {
        console.warn(
          "Content-Length header not available, progress will be estimated"
        );
      }

      // Get response as readable stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Failed to get response reader");
      }

      // Read the data stream with progress tracking
      let receivedBytes = 0;
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Store the chunk
        chunks.push(value);
        receivedBytes += value.length;

        // Calculate and update progress
        if (totalBytes > 0) {
          const progress = Math.round((receivedBytes / totalBytes) * 100);
          setDownloadStates((prev) => ({
            ...prev,
            [music.id]: {
              ...prev[music.id],
              progress,
            },
          }));
        } else {
          // If we don't know total size, just increment progress gradually
          setDownloadStates((prev) => ({
            ...prev,
            [music.id]: {
              ...prev[music.id],
              progress: Math.min((prev[music.id]?.progress || 0) + 1, 95),
            },
          }));
        }
      }

      // Concatenate chunks into a single Uint8Array
      const allChunks = new Uint8Array(receivedBytes);
      let position = 0;
      for (const chunk of chunks) {
        allChunks.set(chunk, position);
        position += chunk.length;
      }

      // Convert to base64
      const base64Data = uint8ArrayToBase64(allChunks);

      // Write the file to storage
      await Filesystem.writeFile({
        path: `music/${fileName}`,
        data: base64Data,
        directory: Directory.Data,
      });

      // Get file info
      const fileInfo = await Filesystem.stat({
        path: `music/${fileName}`,
        directory: Directory.Data,
      });

      // Create metadata
      const storedFile: StoredMusicFile = {
        id: music.id,
        music,
        filePath: `music/${fileName}`,
        fileSize: fileInfo.size,
        downloadDate: new Date().toISOString(),
      };

      // Save metadata
      await Filesystem.writeFile({
        path: `music/${metadataFileName}`,
        data: JSON.stringify(storedFile),
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      // Update states - download completed
      setDownloadStates((prev) => ({
        ...prev,
        [music.id]: {
          isDownloading: false,
          progress: 100,
          isCompleted: true,
          error: undefined,
          filePath: storedFile.filePath,
          fileSize: storedFile.fileSize,
        },
      }));

      // Add to download history
      setDownloadHistory((prev) => {
        const filtered = prev.filter((item) => item.id !== music.id);
        return [...filtered, storedFile];
      });
    } catch (error) {
      if (
        (error instanceof Error && error.message === "Download cancelled") ||
        (error instanceof DOMException && error.name === "AbortError")
      ) {
        setDownloadStates((prev) => ({
          ...prev,
          [music.id]: {
            isDownloading: false,
            progress: 0,
            isCompleted: false,
            error: "Download cancelled",
          },
        }));
      } else {
        console.error("Download error:", error);
        setDownloadStates((prev) => ({
          ...prev,
          [music.id]: {
            isDownloading: false,
            progress: 0,
            isCompleted: false,
            error: `Download failed: ${
              error instanceof Error ? error.message : "Unknown error"
            }`,
          },
        }));
      }
    } finally {
      // Clean up - remove from currently downloading
      setCurrentlyDownloading((prev) => {
        const newDownloading = { ...prev };
        delete newDownloading[music.id];
        return newDownloading;
      });

      setDownloadControllers((prev) => {
        const newControllers = { ...prev };
        delete newControllers[music.id];
        return newControllers;
      });
    }
  }, []);

  const deleteStoredFile = useCallback(async (musicId: string) => {
    try {
      const fileName = `${musicId}.mp3`;
      const metadataFileName = `${musicId}.json`;

      // Delete the music file
      await Filesystem.deleteFile({
        path: `music/${fileName}`,
        directory: Directory.Data,
      });

      // Delete the metadata file
      await Filesystem.deleteFile({
        path: `music/${metadataFileName}`,
        directory: Directory.Data,
      });

      // Remove from history
      setDownloadHistory((prev) => prev.filter((item) => item.id !== musicId));

      // Clear download state
      setDownloadStates((prev) => {
        const newStates = { ...prev };
        delete newStates[musicId];
        return newStates;
      });
    } catch (error) {
      console.error("Failed to delete stored file:", error);
      throw error;
    }
  }, []);

  const getStorageInfo = useCallback(async () => {
    const totalFiles = downloadHistory.length;
    const totalSize = downloadHistory.reduce(
      (sum, file) => sum + file.fileSize,
      0
    );
    return { totalFiles, totalSize };
  }, [downloadHistory]);

  const cancelDownload = useCallback(
    (musicId: string) => {
      const controller = downloadControllers[musicId];
      if (controller) {
        controller.abort();
      }
      // Remove from currently downloading
      setCurrentlyDownloading((prev) => {
        const newDownloading = { ...prev };
        delete newDownloading[musicId];
        return newDownloading;
      });
    },
    [downloadControllers]
  );

  const clearDownload = useCallback((musicId: string) => {
    setDownloadStates((prev) => {
      const newStates = { ...prev };
      delete newStates[musicId];
      return newStates;
    });
  }, []);

  const removeFromHistory = useCallback(
    async (musicId: string) => {
      try {
        await deleteStoredFile(musicId);
      } catch (error) {
        console.error("Failed to remove from history:", error);
      }
    },
    [deleteStoredFile]
  );

  const value: DownloadMusicContextType = {
    downloadStates,
    downloadHistory,
    currentlyDownloading,
    downloadMusic,
    cancelDownload,
    clearDownload,
    removeFromHistory,
    deleteStoredFile,
    getStorageInfo,
  };

  return (
    <DownloadMusicContext.Provider value={value}>
      {children}
    </DownloadMusicContext.Provider>
  );
};

// Helper function to convert Uint8Array to base64
const uint8ArrayToBase64 = (bytes: Uint8Array): string => {
  let binary = "";
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};
